import { createAsyncThunk, createSlice, PayloadAction, unwrapResult } from '@reduxjs/toolkit';
import { RootState } from './store';
import http from '../utils/http';
import { Fact, Axis, DimensionReference } from './AfrSlice';
import { Reference, Part, PartDefinition } from './TaxonomyReferenceSlice'
import { TaxonomyTypeEnum } from './TaxonomyVersionSlice';
import enums from '../utils/enums';
import utilityHelper from '../utils/utilityHelper';
export interface Label {
    roleName: string;
    value: string;
}

export interface DimensionalConceptRef {
    conceptName: string;
    conceptId: string;
    dimensions: Array<DimensionReference> | null;
    taxonomyType: TaxonomyTypeEnum;
    afrYear: number;
}

export interface DimensionalConceptReference {
    concept: Concept;
    dimensions: Array<DimensionReference> | null;
}

export interface Deprecation {
    deprecationDate: string;
    reason: string;
    replacementType: string; // one of simple, aggregateToPart, partToAggregate
    replacements: DimensionalConceptRef[];
}

export interface Concept {
    name: string;
    code: string;
    id: string;
    documentation: string;
    datatype: string;
    domainDatatype: string;
    labels: Array<Label>;
    references: Array<Reference>;
    deprecation: Deprecation | null;
    isAbstract: boolean | null;
    isCredit: boolean | null;
    isDeprecated: boolean | null;
    periodtype: string;
}


export interface ConceptState {
    selectedConcept: Concept | null;
    status: 'idle' | 'loading' | 'failed';
}

export interface ConceptRef {
    conceptId: string;
    taxonomyType: TaxonomyTypeEnum;
    afrYear: number;
}

export interface ConceptDisplayRef {
    conceptName: string;
    conceptId: string;
    taxonomyType: TaxonomyTypeEnum;
    afrYear: number;
}

const initialState: ConceptState = {
    selectedConcept: null,
    status: "idle"
}

export const emptyConcept: Concept = {
        name: "",
        code: "",
        id: "",
        documentation: "",
        datatype: "",
        domainDatatype: "",
        labels: [],
        references: [],
        deprecation: null,
        isAbstract: null,
        isCredit: null,
        isDeprecated: null,
        periodtype: ""
}
export const getLabelValue = function (c: Concept, rname : string ): string | null {
    var result: string | null = null;
    if (!c.labels) {
        return null;
    }
    var lblFound: Label | undefined = c.labels.find(l => l.roleName === rname);
    if (lblFound !== undefined) {
        result = lblFound.value;
    }
    return result;
}
export const getPublicName = function (c: Concept): string {
    var terseLabel: string | null = getLabelValue(c, "terseLabel");
    if (terseLabel) {
        return terseLabel;
    }
    var standardLabel: string | null = getLabelValue(c, "label");
    if (!standardLabel) {
        standardLabel = c.name;
    }
    if (standardLabel) {
        if (c.id.indexOf("BS_") === 0 || c.id.indexOf("Rev_") === 0 || c.id.indexOf("Exp_") === 0 || c.name.indexOf("9") === 0 || c.id.indexOf("FG_") >= 0) {
            return utilityHelper.trimAccountCode(standardLabel);
        }
        return standardLabel;
    }
    if (c.name && c.name.length > 0) {
        return c.name;
    }
    return c.id;
}
export const getPublicAxisName = function (c: Axis): string {
    if (c.name && c.name.length > 0) {
        return c.name;
    }
    return c.id;
}
export const getBooleanConceptAttribute = function (c: any, attr: string): boolean {
    var result = false;
    if (c && c.hasOwnProperty(attr)) {
        result = (c[attr] === true);
    }
    return result;
}
export const getStringConceptAttribute = function (c: any, attr: string): string {
    var result = "";
    if (c && c.hasOwnProperty(attr)) {
        result = c[attr];
    }
    return result;
}
export const isSummableDatatype = function(dtype : string) : boolean {
    var result = false;
    if (dtype && enums.conceptDatatype.hasOwnProperty(dtype)) {
        switch (enums.conceptDatatype[dtype]) {
            case "money":
            case "whole number":
                result = true;
                break;
        }
    }
    return result;
}
export const loadConcept = createAsyncThunk(
    "Taxonomy/GetConceptDetails",
    async (conceptRef : ConceptRef): Promise<Concept | null> => {
        try {
            var cref = conceptRef.conceptId + "^" + conceptRef.afrYear;
            var tt = conceptRef.taxonomyType;
            const result = await http<undefined, Concept>({
                path: `/taxonomy/${tt}/${cref}`,
            });
            if (result.ok && result.parsedBody) {
                return result.parsedBody;
            }
            else {
                return null;
            }
        } catch (ex) {
            return null;
        }
    });

export const getAccountId = function (rowid: string) {
    if (!rowid) {
        return "none";
    }
    var parts = rowid.split("|");
    if (parts.length > 1) {
        return "AccountCode_" + parts[1];
    }
    return parts[0];
}

export const conceptSlice = createSlice({
    name: 'Concept',
    initialState,
    reducers: {
        resetConceptState: (state: any, action: any) => {
            return initialState
        },
        setToSelectedConcept: (state: any, action: any) => {
            state.selectedConcept = action.payload;
        },
    },
    extraReducers: (builder: any) => {
        builder
            .addCase(loadConcept.pending, (state: any) => {
                state.status = "loading";
            })
            .addCase(loadConcept.fulfilled, (state: any, action: any) => {
                state.status = "idle";
                state.selectedConcept = action.payload;
            })
    },
});

export const { resetConceptState, setToSelectedConcept } = conceptSlice.actions;

export const getSelectedConcept = (state: RootState) => state.concept ? state.concept.selectedConcept : null;

export default conceptSlice.reducer;